import { apiClient } from "@/lib/api-client";
import type { EmailCampaign, EmailCampaignType } from "@/types/email";

/** Scheduling, sending and retrying snapshot and render the email on the server first. */
const SNAPSHOT_TIMEOUT_MS = 60_000;

export interface CreateCampaignPayload {
  name: string;
  type: EmailCampaignType;
  subject: string;
  previewText?: string;
  fromName: string;
  fromEmail: string;
  replyToEmail?: string;
  recipientListJson: string;
  estimatedRecipients: number;
  trackOpens: boolean;
  trackClicks: boolean;
  emailDocumentId: string;
}

export type UpdateCampaignPayload = CreateCampaignPayload;

class CampaignService {
  private basePath = "/email-builder/campaigns";

  /** The verified sender address all campaigns are sent from. */
  async getSender(): Promise<{ senderAddress: string }> {
    return apiClient.get(`${this.basePath}/sender`, { requiresAuth: true });
  }

  async getCampaigns(): Promise<EmailCampaign[]> {
    return apiClient.get(`${this.basePath}/account`, { requiresAuth: true });
  }

  async getCampaign(id: string): Promise<EmailCampaign> {
    return apiClient.get(`${this.basePath}/${id}`, { requiresAuth: true });
  }

  async createCampaign(data: CreateCampaignPayload): Promise<EmailCampaign> {
    return apiClient.post(this.basePath, data, { requiresAuth: true });
  }

  async updateCampaign(id: string, data: UpdateCampaignPayload): Promise<EmailCampaign> {
    return apiClient.put(`${this.basePath}/${id}`, data, { requiresAuth: true });
  }

  async deleteCampaign(id: string): Promise<void> {
    return apiClient.delete(`${this.basePath}/${id}`, { requiresAuth: true });
  }

  async scheduleCampaign(id: string, scheduledTime: string): Promise<EmailCampaign> {
    return apiClient.post(`${this.basePath}/${id}/schedule`, { scheduledTime }, { requiresAuth: true, timeoutMs: SNAPSHOT_TIMEOUT_MS });
  }

  async sendNow(id: string): Promise<EmailCampaign> {
    return apiClient.post(`${this.basePath}/${id}/send-now`, {}, { requiresAuth: true, timeoutMs: SNAPSHOT_TIMEOUT_MS });
  }

  async retryCampaign(id: string): Promise<EmailCampaign> {
    return apiClient.post(`${this.basePath}/${id}/retry`, {}, { requiresAuth: true, timeoutMs: SNAPSHOT_TIMEOUT_MS });
  }

  async getCampaignHtml(id: string, mergeTags?: Record<string, string>): Promise<string> {
    const params = mergeTags
      ? Object.entries(mergeTags).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {} as Record<string, string>)
      : undefined;
    return apiClient.get(`${this.basePath}/${id}/html`, { requiresAuth: true, params });
  }
}

export const campaignService = new CampaignService();
